import React, { useMemo, useCallback, useEffect } from 'react';
// eslint-disable-next-line jira/restricted/react-redux
import type { ConnectedComponent } from 'react-redux--next';
import type { Store } from 'redux';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { ConnectionHandler, useFragment, graphql, useMutation } from 'react-relay';
import type { ReactReduxContextInstance } from 'react-redux--next/es/components/Context';

import {
	SectionHeading,
	SectionHeadingTitle,
} from '@jira/issue__section-header/src/section-heading-view.tsx';
import { ExitingPersistence } from '@atlaskit/motion';
import { Stack, xcss, Inline } from '@atlaskit/primitives';
import { useIssueKey, useIssueId } from '@atlassian/jira-issue-context-service/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';

import type { IssueViewRelayFragment } from '@atlassian/jira-issue-fetch-services-common/src/services/issue-agg-data/mainIssueAggQuery.tsx';
import { OUTWARD_LINK_DIRECTION } from '@atlassian/jira-issue-shared-types/src/common/types/linked-issue-type.tsx';
import { connect } from '@atlassian/jira-issue-view-react-redux/src/index.tsx';
import { showLinkIdeaInput } from '@atlassian/jira-issue-view-store/src/actions/link-idea-actions.tsx';
import { IdeaViewSkeleton } from '@atlassian/jira-polaris-component-idea-view-bento-sidebar/src/ui/idea-view/skeleton/index.tsx';
import { RightSidebar } from '@atlassian/jira-polaris-component-idea-view-bento-sidebar/src/ui/right-sidebar/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	useAnalyticsEvents,
	fireTrackAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';

import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import RelayDataID from '@atlassian/relay-data-id';
import { experience } from '@atlassian/jira-polaris-lib-analytics/src/common/constants/experience/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import {
	useActiveEntryPointSubject,
	useActiveEntryPointSubjectActions,
} from '../controllers/entrypoint-reference-store.tsx';
import { useSelectedIdeaKey } from '../controllers/url-selected-idea.tsx';

import { useCrossflowIdeaCardEligible } from '../controllers/use-crossflow-idea-card-eligible.tsx';
import type { ui_issueViewJpdIdeas_IdeasDiscoveryCardEligibilityWrapper_issueViewRelayFragment$key } from './__generated__/ui_issueViewJpdIdeas_IdeasDiscoveryCardEligibilityWrapper_issueViewRelayFragment.graphql';
import type { uiUnlinkIdeaMutation } from './__generated__/uiUnlinkIdeaMutation.graphql';
import type { ui_issueViewJpdIdeas_JPDIdeasPanel$key } from './__generated__/ui_issueViewJpdIdeas_JPDIdeasPanel.graphql';
import SquareAddIconButton from './SquareAddIconButton.tsx';
import { useAdControlsFunctionality } from './crossflow-idea-card/ad-controls/utils.tsx';
import AdControls from './crossflow-idea-card/ad-controls/view.tsx';
import { CrossflowIdeaCard } from './crossflow-idea-card/index.tsx';
import { IdeaCard } from './idea-card/index.tsx';
import { messages } from './messages.tsx';
import { createAddJPDLinkEntrypoint } from './add-idea/entrypoint.tsx';

const PRODUCT_DISCOVERY = 'PRODUCT_DISCOVERY';
const entryPointParams = {};

type Props = {
	issueViewRelayFragment: IssueViewRelayFragment;
	jpdDeliveryIssueLinkTypeId?: string | null;
	shouldShowAddIssueLinks?: boolean;

	onShowLinkIdeaInput: () => void;
};

type IdeaLinksProps = {
	issueId: string;
	issueKey: string;
	summary: string;
	issueTypeIconUrl: string | undefined;
	issueTypeName: string | undefined;
	issueLinkId: string | undefined;
}[];

export const JPDIdeasPanelComponent = ({
	issueViewRelayFragment,
	jpdDeliveryIssueLinkTypeId,
	shouldShowAddIssueLinks = false,
	onShowLinkIdeaInput,
}: Props): React.JSX.Element | undefined => {
	const urlSelectedIdeaKey = useSelectedIdeaKey();
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();

	const { showFlag } = useFlagService();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { entryPointActions, entryPointReferenceSubject: entryPointReferenceSubjectAddJPDIdea } =
		useEntryPoint(createAddJPDLinkEntrypoint, entryPointParams);

	// needs to be migrated to 2 triggers with prop drilling, doing it this way to get perf gains earlier
	// https://atlassian.slack.com/archives/C07JUC8ALCT/p1753768586722859 more context
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (shouldShowAddIssueLinks) {
			entryPointActions.load();
		}
	}, [entryPointActions, shouldShowAddIssueLinks]);

	useEffect(() => {
		return () => {
			if (urlSelectedIdeaKey) {
				experience.ideaViewBentoSidebar.pageSegmentLoad.abort('panel_unmount_query_param');
			} else {
				experience.ideaViewBentoSidebar.pageSegmentLoad.abort('panel_unmount');
			}
		};
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const data = useFragment<ui_issueViewJpdIdeas_JPDIdeasPanel$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment ui_issueViewJpdIdeas_JPDIdeasPanel on JiraIssueLinkConnection {
				__id
				edges {
					node {
						... on JiraIssueLink {
							# eslint-disable-next-line @atlassian/relay/unused-fields
							id
							issueLinkId
							direction
							type {
								linkTypeId
							}
							issue {
								# eslint-disable-next-line @atlassian/relay/unused-fields
								id
								issueId
								key
								fieldsById(
									ids: ["assignee", "issuetype", "priority", "status", "summary", "project"]
								) {
									edges {
										node {
											__typename
											... on JiraSingleLineTextField {
												fieldId
												name
												text
											}
											... on JiraProjectField {
												fieldId
												project {
													projectType
												}
											}
											... on JiraIssueTypeField {
												fieldId
												name
												issueType {
													name
													avatar {
														medium
													}
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		issueViewRelayFragment?.issueLinks,
	);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [unlinkIdeaMutationCommit] = useMutation<uiUnlinkIdeaMutation>(graphql`
		mutation uiUnlinkIdeaMutation($cloudId: ID!, $issueLinkId: ID!) {
			jira {
				deleteIssueLink(cloudId: $cloudId, issueLinkId: $issueLinkId) {
					deletedIds
					success
					errors {
						message
					}
				}
			}
		}
	`);

	const connectionId = data?.__id;

	const onUnlinkModalConfirmed = useCallback(
		(issueLinkId: string) => {
			unlinkIdeaMutationCommit({
				variables: {
					cloudId,
					issueLinkId,
				},
				updater: (store, payload) => {
					const { success, deletedIds } = payload?.jira?.deleteIssueLink ?? {};
					if (success) {
						if (connectionId) {
							const connection = store.get(connectionId);
							if (connection) {
								const nodeIds = deletedIds?.map((deletedId) =>
									RelayDataID({ id: deletedId }, 'JiraIssueLink'),
								);

								nodeIds?.forEach((nodeId) => {
									if (nodeId) {
										ConnectionHandler.deleteNode(connection, nodeId);
									}
								});

								fireTrackAnalytics(createAnalyticsEvent({}), 'idea unlinked');
							}
						}
					}
				},
				onError: () => {
					showFlag({
						type: 'error',
						title: formatMessage(messages.unlinkIdeaErrorFlagTitle),
						description: formatMessage(messages.unlinkIdeaErrorFlagDescription),
						isAutoDismiss: true,
						messageId: 'issue-view-jpd-ideas.ui.show-flag.error.unlink-idea-error',
						messageType: 'transactional',
					});
				},
			});
		},
		[
			cloudId,
			connectionId,
			createAnalyticsEvent,
			unlinkIdeaMutationCommit,
			formatMessage,
			showFlag,
		],
	);

	const ideaLinks = useMemo(
		() =>
			(data?.edges || []).filter((link) =>
				link?.node?.issue?.fieldsById?.edges?.some(
					(edge) =>
						edge?.node?.__typename === 'JiraProjectField' &&
						edge?.node?.project?.projectType === PRODUCT_DISCOVERY,
				),
			),
		[data?.edges],
	);

	const ideaLinksProps: IdeaLinksProps = useMemo(
		() =>
			ideaLinks
				.map((link) => {
					const issue = link?.node?.issue;
					const issueLinkId = link?.node?.issueLinkId;
					const isDeliveryIssueLinkType =
						jpdDeliveryIssueLinkTypeId &&
						link?.node?.type?.linkTypeId === jpdDeliveryIssueLinkTypeId &&
						link?.node?.direction === OUTWARD_LINK_DIRECTION;

					if (!issue?.key || issueLinkId === null) {
						return null;
					}

					if (!isDeliveryIssueLinkType && fg('jpd_idea_panel_in_issue_view')) {
						return null;
					}

					const summaryEdge = issue?.fieldsById?.edges?.find(
						(edge) =>
							edge?.node?.__typename === 'JiraSingleLineTextField' &&
							edge?.node?.fieldId === 'summary',
					);

					const issueTypeEdge = issue?.fieldsById?.edges?.find(
						(edge) => edge?.node?.__typename === 'JiraIssueTypeField',
					);

					const summary =
						(summaryEdge?.node && 'text' in summaryEdge.node && summaryEdge.node.text) || '';
					const issueType =
						issueTypeEdge?.node && 'issueType' in issueTypeEdge.node
							? issueTypeEdge.node.issueType
							: undefined;

					return {
						issueId: issue.issueId,
						issueKey: issue.key,
						summary,
						issueTypeIconUrl: issueType?.avatar?.medium ?? undefined,
						issueTypeName: issueType?.name,
						issueLinkId,
					};
				})
				.filter(Boolean),
		[ideaLinks, jpdDeliveryIssueLinkTypeId],
	);
	const runtimeAddIdeaProps = useMemo(
		() => ({
			ideaLinksProps,
			jpdDeliveryIssueLinkTypeId,
			connectionId,
		}),
		[ideaLinksProps, jpdDeliveryIssueLinkTypeId, connectionId],
	);

	const { setEntryPointReferenceSubject } = useActiveEntryPointSubjectActions();
	const [entryPointReferenceSubject] = useActiveEntryPointSubject();

	const handleOnSidebarClose = useCallback(
		(e: Event) => {
			e.preventDefault();
			e.stopPropagation();

			setEntryPointReferenceSubject(null, true);
		},
		[setEntryPointReferenceSubject],
	);

	const handleOnEntryPointClose = useCallback(() => {
		setEntryPointReferenceSubject(null, true);
	}, [setEntryPointReferenceSubject]);

	useEffect(() => {
		return () => {
			setEntryPointReferenceSubject(null);
		};
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const renderIdeaCards = () => {
		if (ideaLinksProps.length) {
			return (
				<UFOSegment name="issue-jpd-ideas">
					<Stack xcss={containerStyles}>
						{ideaLinksProps.map((linkProps) => (
							<IdeaCard
								key={linkProps.issueKey}
								{...linkProps}
								onUnlinkModalConfirmed={onUnlinkModalConfirmed}
								isUrlSelected={linkProps.issueKey === urlSelectedIdeaKey}
							/>
						))}
					</Stack>
					<ExitingPersistence appear>
						{entryPointReferenceSubject && (
							<RightSidebar onClose={handleOnSidebarClose}>
								<JiraEntryPointContainer
									id="async-jpd-idea-sidebar-view"
									packageName="jira-polaris-component-idea-view-bento-sidebar"
									entryPointReferenceSubject={entryPointReferenceSubject}
									runtimeProps={{
										onClose: handleOnEntryPointClose,
									}}
									fallback={<IdeaViewSkeleton />}
									errorFallback="flag"
								/>
							</RightSidebar>
						)}
					</ExitingPersistence>
				</UFOSegment>
			);
		}
	};

	if (ideaLinksProps.length || shouldShowAddIssueLinks) {
		return (
			<ContextualAnalyticsData
				sourceName="linkedIdeasPanel"
				sourceType={SCREEN}
				attributes={{
					linkedIdeasCount: ideaLinks.length,
				}}
			>
				<Stack space="space.100">
					<SectionHeading>
						<SectionHeadingTitle
							{...(fg('jfp_a11y_team_missing_label_for_form_field') && { id: 'ideas-field-label' })}
						>
							{formatMessage(messages.ideasTitle)}
						</SectionHeadingTitle>
						<SquareAddIconButton
							label={formatMessage(messages.plusButtonLabel)}
							onClick={onShowLinkIdeaInput}
						/>
					</SectionHeading>
					{renderIdeaCards()}
					{shouldShowAddIssueLinks && (
						<JiraEntryPointContainer
							entryPointReferenceSubject={entryPointReferenceSubjectAddJPDIdea}
							id="jpd-add-idea-entrypoint"
							packageName="jpd-add-idea-entrypoint"
							fallback={null}
							runtimeProps={runtimeAddIdeaProps}
						/>
					)}
					<FireScreenAnalytics />
				</Stack>
			</ContextualAnalyticsData>
		);
	}

	return <IdeasDiscoveryCardEligibilityWrapper issueViewRelayFragment={issueViewRelayFragment} />;
};

const IdeasDiscoveryCardEligibilityWrapper = ({
	issueViewRelayFragment,
}: {
	issueViewRelayFragment: IssueViewRelayFragment;
}) => {
	const data =
		useFragment<ui_issueViewJpdIdeas_IdeasDiscoveryCardEligibilityWrapper_issueViewRelayFragment$key>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment ui_issueViewJpdIdeas_IdeasDiscoveryCardEligibilityWrapper_issueViewRelayFragment on JiraIssue {
					canLinkIssues: hasProjectPermission(permission: LINK_ISSUES)
					statusCategory {
						statusCategoryId @required(action: LOG)
					}
					issueTypeField {
						name @required(action: LOG)
						issueType {
							hierarchy {
								level
							}
						}
					}
				}
			`,
			issueViewRelayFragment,
		);

	const eligibilityProps = {
		issueType: {
			name: data?.issueTypeField?.name,
			level: data?.issueTypeField?.issueType?.hierarchy?.level,
		},
		statusCategoryId: data?.statusCategory?.statusCategoryId,
		canLinkIssues: data?.canLinkIssues,
	};

	const shouldShowIdeaDiscoveryCard = useCrossflowIdeaCardEligible(eligibilityProps);

	if (!shouldShowIdeaDiscoveryCard) {
		return null;
	}

	return <IdeasDiscoveryCard />;
};

const IdeasDiscoveryCard = () => {
	const { formatMessage } = useIntl();

	const { dismiss, isDismissed } = useAdControlsFunctionality({
		userPropertyCrossflowIdeasCard: 'hasDismissedCrossflowIdeaCard',
	});

	const currentIssueKey = useIssueKey();
	const currentIssueId = useIssueId();

	if (isDismissed || !currentIssueKey || !currentIssueId) {
		return null;
	}

	return (
		<UFOSegment name="issue-jpd-idea-discovery-card">
			<JSErrorBoundary
				id="CrossflowIdeaCard"
				packageName="issue-view-ideas-panel"
				teamName="galileo"
				fallback="unmount"
			>
				<ContextualAnalyticsData
					sourceName="crossflowIdeaCard"
					sourceType={SCREEN}
					attributes={{
						issueKey: currentIssueKey,
					}}
				>
					<Stack space="space.100">
						<SectionHeading>
							<Inline space="space.100" alignBlock="center">
								<SectionHeadingTitle>{formatMessage(messages.ideasTitle)}</SectionHeadingTitle>
							</Inline>
							<AdControls onDismissClick={dismiss} />
						</SectionHeading>
						<Stack xcss={containerStyles}>
							<CrossflowIdeaCard
								inwardIssueKey={currentIssueKey}
								inwardIssueId={currentIssueId}
								onDismissAd={dismiss}
							/>
						</Stack>
						<FireScreenAnalytics />
					</Stack>
				</ContextualAnalyticsData>
			</JSErrorBoundary>
		</UFOSegment>
	);
};

const JPDIdeaPanelWrapped = (props: Props): React.JSX.Element => {
	return (
		<JSErrorBoundary
			id="issueViewJpdIdeas"
			fallback="unmount"
			teamName="JPD - Juno"
			packageName="@atlassian/jira-issue-view-jpd-ideas"
		>
			<JPDIdeasPanelComponent {...props} />
		</JSErrorBoundary>
	);
};

export const JPDIdeasPanel: ConnectedComponent<
	(props: Props) => React.JSX.Element,
	{
		issueViewRelayFragment: IssueViewRelayFragment;
		jpdDeliveryIssueLinkTypeId?: string | null | undefined;
		shouldShowAddIssueLinks?: boolean | undefined;
		context?: ReactReduxContextInstance | undefined;
		store?: Store | undefined;
	}
> = connect(
	() => ({}),
	(dispatch) => ({
		onShowLinkIdeaInput: () => {
			dispatch(showLinkIdeaInput());
		},
	}),
)(JPDIdeaPanelWrapped);

const containerStyles = xcss({
	paddingRight: 'space.025',
});

// needed for importCond, remove default export once the importCond is removed (fg: fy26_nike_box_bundle_decrease_project)
export default JPDIdeasPanel;
